import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Mail, ExternalLink, Navigation } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const contactInfo = [
  {
    icon: MapPin,
    title: 'Adresse',
    content: 'Av. Ritz 33, 1950 Sion',
    action: 'Itinéraire',
    href: 'https://www.google.com/maps/dir/?api=1&destination=Av.+Ritz+33,+1950+Sion,+Suisse',
  },
  {
    icon: Phone,
    title: 'Téléphone',
    content: '+41 27 323 23 16',
    action: 'Appeler',
    href: 'tel:+41273232316',
  },
  {
    icon: Mail,
    title: 'Email',
    content: 'info@alma-fleurs-sion.ch',
    action: 'Envoyer',
    href: 'mailto:info@alma-fleurs-sion.ch',
  },
];

const hours = [
  { day: 'Lundi', time: '09:00 - 18:30' },
  { day: 'Mardi', time: '09:00 - 18:30' },
  { day: 'Mercredi', time: '09:00 - 18:30' },
  { day: 'Jeudi', time: '09:00 - 18:30' },
  { day: 'Vendredi', time: '09:00 - 18:30' },
  { day: 'Samedi', time: '09:00 - 17:00' },
  { day: 'Dimanche', time: 'Fermé' },
];

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      toast.success('Message envoyé !', {
        description: 'Nous vous répondrons dans les plus brefs délais.',
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section
      data-testid="contact-section"
      className="py-24 md:py-32 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-accent font-accent italic text-lg mb-4"
          >
            Nous contacter
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-primary tracking-tight"
          >
            Rendez-nous visite
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info & Form */}
          <div className="space-y-10">
            {/* Contact Cards */}
            <div className="grid sm:grid-cols-3 gap-4">
              {contactInfo.map((info, index) => (
                <motion.a
                  key={info.title}
                  href={info.href}
                  target={info.href.startsWith('http') ? '_blank' : undefined}
                  rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`contact-${info.title.toLowerCase()}`}
                  className="group bg-white p-6 rounded-2xl border border-primary/5 hover:shadow-lg hover:-translate-y-1 transition-all"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                    <info.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-medium text-primary mb-1">{info.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3">{info.content}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-accent font-medium">
                    {info.action}
                    <ExternalLink className="w-3 h-3" />
                  </span>
                </motion.a>
              ))}
            </div>

            {/* Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white p-6 rounded-2xl border border-primary/5"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-primary">Horaires d'ouverture</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {hours.map((item) => (
                  <div
                    key={item.day}
                    className={`flex justify-between py-2 px-3 rounded-lg ${
                      item.time === 'Fermé' ? 'bg-red-50 text-red-600' : 'bg-secondary/50'
                    }`}
                  >
                    <span className="font-medium text-sm">{item.day}</span>
                    <span className="text-sm">{item.time}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              onSubmit={handleSubmit}
              className="bg-white p-6 md:p-8 rounded-2xl border border-primary/5"
              data-testid="contact-form"
            >
              <h3 className="font-serif text-xl font-semibold text-primary mb-6">Envoyez-nous un message</h3>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Input
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="contact-name-input"
                      className="rounded-xl border-primary/10 focus:border-accent"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Votre email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="contact-email-input"
                      className="rounded-xl border-primary/10 focus:border-accent"
                    />
                  </div>
                </div>
                <Input
                  type="tel"
                  placeholder="Votre téléphone (optionnel)"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contact-phone-input"
                  className="rounded-xl border-primary/10 focus:border-accent"
                />
                <Textarea
                  placeholder="Votre message..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  data-testid="contact-message-input"
                  className="rounded-xl border-primary/10 focus:border-accent resize-none"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                  className="w-full rounded-full py-6 bg-primary hover:bg-primary/90 transition-all"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer le message'}
                </Button>
              </div>
            </motion.form>
          </div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="sticky top-32">
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-primary/10 h-[500px] lg:h-[700px]">
                <iframe
                  data-testid="google-map"
                  title="Alma Fleur SA - Localisation"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2769.1234567890!2d7.358!3d46.233!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAv.%20Ritz%2033%2C%201950%20Sion!5e0!3m2!1sfr!2sch!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              
              {/* Floating Action Button */}
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=Av.+Ritz+33,+1950+Sion,+Suisse"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="map-directions-btn"
                className="absolute bottom-6 right-6"
              >
                <Button className="rounded-full gap-2 shadow-lg bg-primary hover:bg-primary/90 px-6 py-6">
                  <Navigation className="w-5 h-5" />
                  Itinéraire
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
